import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AppService } from '../app.service';
import { AdminTabLink, buildAdminTabLinks } from './admin-route.config';
import { AdminBreadcrumbService } from './shared/admin-breadcrumb.service';
import { AdminScreenContextService } from './shared/admin-screen-context.service';
import { isSsoOnPrivilegedList } from './shared/admin-privilege.util';

export type { AdminTabLink };

@Component({
    selector: 'gtw-admin-shell',
    templateUrl: './admin-shell.component.html',
    styleUrls: ['./admin-shell.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AdminShellComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tabContainer') tabContainerRef!: ElementRef;

  adminLinks: AdminTabLink[] = [];
  activeLinks: AdminTabLink[] = [];

  userAccessLevel = 0;
  ssoId = '';
  section: 'admin' | 'userSettings' = 'admin';

  disableScrollLeft = true;
  disableScrollRight = true;
  hideScrollButtons = true;
  hideDropDown = true;
  showDropdownMenu = false;
  dropdownTabs: AdminTabLink[] = [];

  private privilegedSsoList: unknown = '';
  private efilePrivList = '';
  private regenerateDataPrivList = '';
  private destroy$ = new Subject<void>();

  private scrollIntervalId: any = null;
  private isScrollHolding = false;
  private scrollByPixels = 50;
  private winResizeTimeout: any;
  private resizeHandler = () => this.onWindowResize();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private adminBreadcrumb: AdminBreadcrumbService,
    private cdr: ChangeDetectorRef,
    private adminScreenContext: AdminScreenContextService,
    private matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.section = this.route.snapshot.data['section'] || 'admin';
    this.initUserData();
    this.buildTabs();
    this.syncBreadcrumbFromRoute();

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Defensive: do not wipe row context while a dialog is still mounted.
        // Some flows (e.g. shortcut links inside a modal) can trigger
        // NavigationEnd before the dialog's ngOnDestroy runs.
        if (this.matDialog.openDialogs.length === 0) {
          this.adminScreenContext.setRowData({});
        }
        this.syncBreadcrumbFromRoute();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initScrollableTabs(), 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.resizeHandler);
    this.scrollButtonUp();
  }

  isActive(tab: AdminTabLink): boolean {
    const url = this.router.url.split('?')[0];
    const tabLink = tab.parentTab || tab.link;
    const prefix = this.section === 'userSettings' ? '/userSettings/' : '/admin/';
    return url === prefix + tabLink || url.startsWith(prefix + tabLink + '/');
  }

  navigateTo(tab: AdminTabLink): void {
    const segments = (tab.link || '').split('/').filter(Boolean);
    const base = this.section === 'userSettings' ? '/userSettings' : '/admin';
    this.router.navigate([base, ...segments]);
    setTimeout(() => this.scrollTabIntoView(), 100);
  }

  isTabVisible(tab: AdminTabLink): boolean {
    if (this.userAccessLevel < tab.access) {
      return false;
    }
    return this.checkPrivileges(tab);
  }

  // ---- Scrollable tabset methods ----

  scrollButtonDown(direction: 'left' | 'right', event: MouseEvent): void {
    event.stopPropagation();
    this.isScrollHolding = true;
    const container = this.tabContainerRef?.nativeElement;
    if (!container) return;
    const realScroll = direction === 'left' ? -this.scrollByPixels : this.scrollByPixels;
    this.scrollTo(container, realScroll, 150, () => this.reCalcSides(), true);
    this.scrollIntervalId = setInterval(() => {
      if (this.isScrollHolding) {
        this.scrollTo(container, realScroll, 150, () => this.reCalcSides(), true);
        if ((direction === 'left' && this.disableScrollLeft) ||
            (direction === 'right' && this.disableScrollRight)) {
          this.scrollButtonUp();
        }
      }
    }, 100);
  }

  scrollButtonUp(): void {
    this.isScrollHolding = false;
    if (this.scrollIntervalId) {
      clearInterval(this.scrollIntervalId);
      this.scrollIntervalId = null;
    }
  }

  onTabContainerScroll(): void {
    this.reCalcSides();
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showDropdownMenu = !this.showDropdownMenu;
    this.cdr.detectChanges();
    if (this.showDropdownMenu) {
      const closeHandler = () => {
        this.showDropdownMenu = false;
        this.cdr.detectChanges();
        document.removeEventListener('click', closeHandler);
      };
      setTimeout(() => document.addEventListener('click', closeHandler), 0);
    }
  }

  activateDropdownTab(tab: AdminTabLink): void {
    this.showDropdownMenu = false;
    this.navigateTo(tab);
    setTimeout(() => this.scrollTabIntoView(), 500);
  }

  private initScrollableTabs(): void {
    this.recalcScrollButtons();
    setTimeout(() => this.scrollTabIntoView(), 500);
    window.removeEventListener('resize', this.resizeHandler);
    window.addEventListener('resize', this.resizeHandler);
  }

  private recalcScrollButtons(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container) {
      this.hideScrollButtons = true;
      this.hideDropDown = true;
      return;
    }
    const overflows = container.scrollWidth > container.offsetWidth;
    this.hideScrollButtons = !overflows;
    this.hideDropDown = !overflows;
    if (!this.hideScrollButtons) {
      this.dropdownTabs = this.activeLinks.filter(t => this.isTabVisible(t));
      this.reCalcSides();
    } else {
      this.dropdownTabs = [];
    }
    this.cdr.detectChanges();
  }

  private reCalcSides(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideScrollButtons) return;
    this.disableScrollRight = container.scrollLeft >= container.scrollWidth - container.offsetWidth;
    this.disableScrollLeft = container.scrollLeft <= 0;
    this.cdr.detectChanges();
  }

  private scrollTabIntoView(): void {
    const container = this.tabContainerRef?.nativeElement;
    if (!container || this.hideScrollButtons) return;
    const activeTab = container.querySelector('li.active') as HTMLElement;
    if (!activeTab) return;
    const containerRect = container.getBoundingClientRect();
    const rightPosition = activeTab.getBoundingClientRect().left + activeTab.getBoundingClientRect().width - containerRect.left;
    const leftPosition = activeTab.getBoundingClientRect().left - containerRect.left;
    if (leftPosition < 0) {
      this.scrollTo(container, leftPosition, 700, () => this.reCalcSides());
    } else if (rightPosition > container.offsetWidth) {
      const dif = rightPosition - container.offsetWidth;
      this.scrollTo(container, dif, 700, () => this.reCalcSides());
    }
  }

  private scrollTo(element: HTMLElement, change: number, duration: number, callback: () => void, isLinear = false): void {
    const start = element.scrollLeft;
    const increment = 20;
    const animateScroll = (elapsedTime: number) => {
      elapsedTime += increment;
      let position: number;
      if (isLinear) {
        position = change * elapsedTime / duration + start;
      } else {
        let t = elapsedTime / (duration / 2);
        if (t < 1) {
          position = change / 2 * t * t + start;
        } else {
          t--;
          position = -change / 2 * (t * (t - 2) - 1) + start;
        }
      }
      element.scrollLeft = position;
      if (elapsedTime < duration) {
        setTimeout(() => animateScroll(elapsedTime), increment);
      } else {
        callback();
      }
    };
    animateScroll(0);
  }

  private onWindowResize(): void {
    clearTimeout(this.winResizeTimeout);
    this.winResizeTimeout = setTimeout(() => {
      this.recalcScrollButtons();
      this.scrollTabIntoView();
    }, 250);
  }

  private initUserData(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (userSettings?.user) {
      this.userAccessLevel = userSettings.user.system_access_level || 0;
      this.ssoId = userSettings.user.sso_id || '';
    }
    const serverConfig = this.appService.SERVER_CONFIG;
    if (serverConfig?.configSettings) {
      this.privilegedSsoList = serverConfig.configSettings.privileged_sso_list || '';
      this.efilePrivList =
        (serverConfig.configSettings.can_migrate_efile || '') +
        (serverConfig.configSettings.can_signoff_efile || '');
      this.regenerateDataPrivList = serverConfig.configSettings.can_regenrate_fir_form || '';
    }
  }

  private buildTabs(): void {
    this.adminLinks = buildAdminTabLinks();
    this.activeLinks = this.adminLinks;
  }

  private checkPrivileges(tab: AdminTabLink): boolean {
    switch (tab.id) {
      case 'gold-sync':
        return isSsoOnPrivilegedList(this.ssoId, this.privilegedSsoList);
      case 'e-file':
        return this.efilePrivList.includes(this.ssoId);
      case 'regenerate-data':
        return this.regenerateDataPrivList.includes(this.ssoId);
      default:
        return true;
    }
  }

  /** Sync header breadcrumb from `route.data.breadcrumb` (see AdminRoutingModule). */
  private syncBreadcrumbFromRoute(): void {
    const labels = this.adminBreadcrumb.resolveLabels(this.route);
    this.adminBreadcrumb.publishToHeader(labels);
  }
}
